import dayjs, { Dayjs } from "dayjs"

/** Anything the reservation form may be holding for a date: the raw "YYYY-MM-DD" the API
 * seeded it with, or the Dayjs the date picker swaps in once the host touches the field. */
export type ReservationDateValue = string | Dayjs | Date | null | undefined

/**
 * Nights between two dates, or 0 when the range is empty, backwards, or not yet readable.
 *
 * A stay of "no nights" is never priced, so 0 is the safe answer for every unusable input
 * rather than a NaN that would reach the total field.
 */
export function nightsBetween(
	checkIn: ReservationDateValue,
	checkOut: ReservationDateValue
): number {
	if (checkIn == null || checkOut == null) return 0

	const from = dayjs(checkIn as never)
	const to = dayjs(checkOut as never)
	if (!from.isValid() || !to.isValid()) return 0

	const nights = to.startOf("day").diff(from.startOf("day"), "day")
	return nights > 0 ? nights : 0
}

/**
 * The stay's total at a given nightly rate.
 *
 * MS-589 — this exists because the edit form used to derive its night count from the dates the
 * reservation was LOADED with, so lengthening a booking left the total where it was. The backend
 * reads that total as the host's price for the whole stay and recognises an extension by the
 * difference against the price already paid; an unchanged total therefore read as "nothing to
 * collect", the extension was never detected, and the guest got the ordinary «تم تعديل حجزك»
 * email instead of the payment request. The nights must come from the dates in the FORM.
 */
export function stayTotalPrice(
	nightPrice: number | string | undefined | null,
	checkIn: ReservationDateValue,
	checkOut: ReservationDateValue
): number {
	const rate = Number(nightPrice)
	if (nightPrice == null || nightPrice === "" || !Number.isFinite(rate)) return 0

	const nights = nightsBetween(checkIn, checkOut)
	if (nights <= 0) return 0

	const total = rate * nights
	return total > 0 ? total : 0
}
